import React, { useState, useEffect, useCallback } from 'react';
import { useStudySpots } from '../../hooks/useStudySpots';
import FilterPanel from '../../components/studySpots/FilterPanel';
import SpotGrid from '../../components/studySpots/SpotGrid';
import { useLocation } from 'react-router-dom';
import '../../styles/touchDevice.css';
import { SlidersHorizontal } from 'lucide-react';

const presetFilters = {
  focusMode: {
    noiseLevel: 'quiet',
    spaceType: ['Study Carrel', 'Independent Study Room'],
  },
  groupStudy: {
    noiseLevel: 'moderate',
    spaceType: ['Group Study Room'],
  },
  casual: {
    spaceType: ['Open Space'],
  }
};

const SearchPage = () => {
  const location = useLocation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activePreset, setActivePreset] = useState(null);
  const [filters, setFilters] = useState({
    location: [],  
    noiseLevel: '',
    spaceType: [],
    features: []
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const building = params.get('building');
    
    if (building) {
      setFilters(prev => ({
        ...prev,
        location: [building]
      }));
    }
  }, [location]);

  const { spots, loading } = useStudySpots(filters);

  const handlePresetClick = useCallback((preset) => {
    if (activePreset === preset) {
      // If clicking the active preset, clear filters
      setFilters({
        location: [],
        noiseLevel: '',
        spaceType: [],
        features: []
      });
      setActivePreset(null);
    } else {
      // Apply the new preset
      setFilters({
        location: [],
        noiseLevel: '',
        spaceType: [],
        features: [],
        ...presetFilters[preset]
      });
      setActivePreset(preset);
    }
  }, [activePreset]);

  const getButtonClasses = (preset) => {
    const baseClasses = "px-6 py-2.5 rounded-full border text-sm font-medium transition-all duration-200";
    
    return activePreset === preset
      ? `${baseClasses} bg-white text-black border-white`
      : `${baseClasses} bg-transparent text-gray-400 border-gray-800 hover:border-gray-600 hover:text-white`;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Find your spot</h1>
            <p className="text-gray-400">Discover the perfect environment for your study session</p>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsFilterOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
            >
              <SlidersHorizontal size={18} />
              <span>Filters</span>
            </button>
          </div>
        </div>

        {/* Preset Filters */}
        <div className="flex flex-wrap gap-3 mb-10">
          <button
            onClick={() => handlePresetClick('focusMode')}
            className={getButtonClasses('focusMode')}
          >
            Locked In 🔒
          </button>
          <button
            onClick={() => handlePresetClick('groupStudy')}
            className={getButtonClasses('groupStudy')}
          >
            Group Study 👥
          </button>
          <button
            onClick={() => handlePresetClick('casual')}
            className={getButtonClasses('casual')}
          >
            Casual ☕
          </button>
        </div>

        {/* Results */}
        <SpotGrid spots={spots} loading={loading} />

        <FilterPanel
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          setFilters={setFilters}
        />
      </div>
    </div>
  );
};

export default SearchPage;